import { useState, useEffect, useCallback } from 'preact/hooks'
import { parseSpotsDisplay, widgetSpotsText } from '../../core/spots-display'
import { apiClient, authStore, InitData, User } from '../../core'
import { t, intlLocale, dayNames, formatMoney, plural } from '../../core/i18n'
import { classPriceDisplay } from '../../core/members-only'
import { dagNoegle, fmtDato, fmtTid, studietsTidszone, ugedagForNoegle } from '../../core/tidszone'
import { BookingModal, type BookableClass } from '../../components/BookingModal'

interface ClassData {
  /** Fri holdtype på den indloggedes abonnement (OPG-0079): vis «Med i dit abonnement». */
  includedInSubscription?: boolean
  id: string
  name: string
  description: string | null
  type: string
  startTime: string
  endTime: string
  maxParticipants: number
  currentParticipants: number
  dropInPriceDkk: number
  room: string | null
  instructorName: string | null
  waitlistEnabled: boolean
  waitlistCount: number
  membershipRequired?: boolean
  excludeFromMembership?: boolean
  /** «Gratis hold» (OPG-0098): ingen betaling, aldrig et klip. */
  isFree?: boolean
  clipCost?: number
  classTemplateId?: string | null
  guestsAllowed?: boolean
  maxGuests?: number
  fullVenueAvailable?: boolean
  fullVenuePriceDkk?: number | null
  /** Holdets egen afmeldingsfrist i timer (null = den generelle), fra widget-API'et. */
  ownCancellationHours?: number | null
  isBooked: boolean
  isFull: boolean
}

interface ScheduleWidgetProps {
  initData: InitData
  /** Only show classes carrying these hold-mærkat slugs. Empty = all. */
  holdTypeSlugs?: string[]
  /** Only show classes from this one class template. */
  templateId?: string | null
  /** Only show classes within the next N weeks (1-52). */
  weeks?: number | null
  /** guests-only="true": gæste-fokuseret booking (stepperen vises åben). */
  guestsOnly?: boolean
}

// Dage, datoer og klokkeslæt følger studiets tidszone (initData.timezone), ikke UTC og
// ikke den besøgendes browser (core/tidszone.ts). Før blev holdene grupperet på
// `startTime.slice(0, 10)` (UTC-datoen), så et hold kl. 00:00 dansk tid stod under dagen før.

/**
 * ScheduleWidget — auth-aware bookable schedule grouped by day.
 * Mirrors HS-native weekly-schedule layout.
 *
 * Booking runs through the shared BookingModal (same component the
 * weekly-schedule and upcoming-classes widgets use), so klip-pris,
 * medlemskrav og betalings-redirect opfører sig ens overalt. Tidligere havde
 * denne widget sin egen indbyggede modal, der kun kunne vise rå fejl på
 * priced/medlems-hold — den er nu fjernet til fordel for den delte modal.
 */
export function ScheduleWidget({ initData, holdTypeSlugs = [], templateId = null, weeks = null, guestsOnly = false }: ScheduleWidgetProps) {
  const s = t(initData.locale)
  const spotsMode = parseSpotsDisplay(initData.features?.spotsDisplay)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)
  const DAY_LABELS = dayNames(initData.locale)
  const [classes, setClasses] = useState<ClassData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(authStore.getUser())
  const [selected, setSelected] = useState<ClassData | null>(null)
  // Første holddato UDEN for `weeks`-vinduet. Se kommentaren ved opslaget nedenfor.
  const [nextStart, setNextStart] = useState<string | null>(null)

  // Subscribe to auth changes so the user badge + isBooked markers refresh
  // after a login (the shared BookingModal writes to authStore on login).
  useEffect(() => authStore.subscribe(setUser), [])

  // Stable primitive key so the array prop doesn't re-fire the effect each render.
  const slugsKey = holdTypeSlugs.join(',')
  const fetchClasses = useCallback(async () => {
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({ calendar: 'true' })
    if (slugsKey) params.set('holdTypeSlugs', slugsKey)
    if (templateId) params.set('templateId', templateId)
    if (weeks) params.set('weeks', String(weeks))

    const result = await apiClient.get<ClassData[]>(`/api/widget/classes?${params.toString()}`, !!user)

    if (result.error) {
      setError(result.error)
    } else if (result.data) {
      setClasses(result.data)

      // `weeks` filtrerer SERVERSIDE. Et for kort vindue giver derfor nul hold
      // og en widget der siger «ingen hold planlagt», selvom studiet har fyldt
      // sæsonen. playgroundlx havde `weeks="1"` på sin side 29-09-2026 med 93
      // hold fra 12. oktober: hver besøgende fik at vide at der ikke var noget.
      // Er vinduet tomt, spørger vi én gang mere uden det, så vi i stedet kan
      // sige HVORNÅR holdene starter. Kun i det tomme tilfælde, så den normale
      // vej stadig er én forespørgsel.
      if (result.data.length === 0 && weeks) {
        const uden = new URLSearchParams(params)
        uden.delete('weeks')
        const alle = await apiClient.get<ClassData[]>(`/api/widget/classes?${uden.toString()}`, !!user)
        const foerste = (alle.data ?? [])
          .map(c => c.startTime)
          .sort()[0]
        setNextStart(foerste ?? null)
      } else {
        setNextStart(null)
      }
    }

    setLoading(false)
  }, [user, slugsKey, templateId, weeks])

  useEffect(() => {
    fetchClasses()
  }, [fetchClasses])

  // Gruppér på datoen (yyyy-mm-dd) i studiets tidszone.
  const grouped = new Map<string, ClassData[]>()
  for (const cls of classes) {
    const key = dagNoegle(cls.startTime, tz)
    const list = grouped.get(key) || []
    list.push(cls)
    grouped.set(key, list)
  }
  const sortedDays = Array.from(grouped.keys()).sort()

  const modalKlass: BookableClass | null = selected ? {
    id: selected.id,
    name: selected.name,
    startTime: selected.startTime,
    endTime: selected.endTime,
    instructorName: selected.instructorName,
    room: selected.room,
    description: selected.description,
    maxParticipants: selected.maxParticipants,
    currentParticipants: selected.currentParticipants,
    dropInPriceDkk: selected.dropInPriceDkk,
    waitlistEnabled: selected.waitlistEnabled,
    membershipRequired: selected.membershipRequired,
    excludeFromMembership: selected.excludeFromMembership,
    isFree: selected.isFree,
    clipCost: selected.clipCost,
    classTemplateId: selected.classTemplateId,
    guestsAllowed: selected.guestsAllowed,
    maxGuests: selected.maxGuests,
    fullVenueAvailable: selected.fullVenueAvailable,
    fullVenuePriceDkk: selected.fullVenuePriceDkk,
    // Uden denne linje fik booking-vinduet aldrig fristen, og «Særlig afmeldingsfrist»
    // blev aldrig vist i den indlejrede widget (fundet af /ship 14-10-2026).
    ownCancellationHours: selected.ownCancellationHours,
    isBooked: selected.isBooked,
  } : null

  return (
    <div>
      <style>{styles}</style>

      {/* Loading skeleton — 3 day-groups × 2 rows */}
      {loading && [0, 1, 2].map(i => (
        <div key={i} class="hsw-sch-day-group">
          <div class="hsw-sch-skel hsw-sch-skel-label" />
          <div class="hsw-sch-skel hsw-sch-skel-row" />
          <div class="hsw-sch-skel hsw-sch-skel-row" />
        </div>
      ))}

      {/* Error */}
      {!loading && error && (
        <div class="hsw-sch-error" role="alert">
          <span class="hsw-sch-error-icon" aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && classes.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📅</div>
          <p>
            {nextStart
              ? s.nextClassesStart.replace('{date}', new Intl.DateTimeFormat(dateLocale, {
                  weekday: 'long', day: 'numeric', month: 'long', timeZone: tz,
                }).format(new Date(nextStart)))
              : s.noClassesPlanned}
          </p>
        </div>
      )}

      {!loading && !error && classes.length > 0 && user && (
        <div class="hsw-sch-user-badge">
          {s.loggedInAs} <strong>{user.firstName}</strong>
        </div>
      )}

      {!loading && !error && classes.length > 0 && sortedDays.map(dateKey => {
        const dayEntries = grouped.get(dateKey)!
        const day = ugedagForNoegle(dateKey)
        return (
          <div key={dateKey} class="hsw-sch-day-group">
            <div class="hsw-sch-day-label">
              {DAY_LABELS[day]} {fmtDato(dayEntries[0].startTime, dateLocale, tz)}
            </div>
            {dayEntries.map(cls => {
              const spotsLeft = cls.maxParticipants - cls.currentParticipants
              const isBooked = cls.isBooked
              const isFull = cls.isFull
              const canWaitlist = isFull && cls.waitlistEnabled
              const isHardFull = isFull && !cls.waitlistEnabled

              // Klip-pris vises kun når holdet koster mere end standard-1-klip
              // og er klip-berettiget — ellers vises kr-drop-in (hvis relevant).
              const clipBadge = (cls.clipCost ?? 1) > 1 && !cls.excludeFromMembership && !cls.isFree
                ? plural(s.clipCostShort, cls.clipCost ?? 0, initData.locale)
                : null
              // Members-only drop-in (fx Saunagus) viser prisen; fælles regel i core/members-only.
              const priceKind = classPriceDisplay(cls).kind
              const showKr = priceKind === 'price'

              let cta: preact.JSX.Element
              if (isBooked) {
                // Skal være en knap, ikke et <span>: uden onClick kunne modalen
                // aldrig åbnes for et booket hold, og dermed var afbud helt
                // uopnåeligt i netop denne widget — de tre andre booking-widgets
                // åbner modalen og viser "Afmeld". Målt på staging: klik på både
                // rækken og badgen gav ingen modal.
                cta = (
                  <button
                    type="button"
                    class="hsw-sch-cta hsw-sch-cta-booked"
                    onClick={() => setSelected(cls)}
                  >
                    {s.booked} ✓
                  </button>
                )
              } else if (isHardFull) {
                cta = <span class="hsw-sch-cta hsw-sch-cta-full">{s.full}</span>
              } else if (canWaitlist) {
                cta = (
                  <button
                    type="button"
                    class="hsw-sch-cta hsw-sch-cta-waitlist"
                    onClick={() => setSelected(cls)}
                  >
                    {s.waitlist}
                  </button>
                )
              } else {
                cta = (
                  <button
                    type="button"
                    class="hsw-sch-cta hsw-sch-cta-book"
                    onClick={() => setSelected(cls)}
                  >
                    {s.book} →
                  </button>
                )
              }

              return (
                <div
                  key={cls.id}
                  class={`hsw-sch-row${isBooked ? ' hsw-sch-row-booked' : ''}${isHardFull ? ' hsw-sch-row-full' : ''}`}
                >
                  <div class="hsw-sch-time">
                    {fmtTid(cls.startTime, dateLocale, tz)} – {fmtTid(cls.endTime, dateLocale, tz)}
                  </div>
                  <div class="hsw-sch-info">
                    <div class="hsw-sch-name">{cls.name}</div>
                    <div class="hsw-sch-meta">
                      {[cls.instructorName, cls.room].filter(Boolean).join(' · ')}
                      {(cls.instructorName || cls.room) ? ' · ' : ''}
                      <span
                        class={`hsw-sch-spots${isHardFull ? ' hsw-sch-spots-full' : ''}${canWaitlist ? ' hsw-sch-spots-waitlist' : ''}`}
                      >
                        {isHardFull
                          ? s.fullyBooked
                          : canWaitlist
                            ? `${s.waitlist} (${cls.waitlistCount})`
                            : widgetSpotsText(spotsLeft, cls.maxParticipants, spotsMode, s, () => `${spotsLeft} ${spotsLeft === 1 ? s.spot : s.spots}`)}
                      </span>
                    </div>
                  </div>
                  <div class="hsw-sch-cta-wrap">
                    <div class="hsw-sch-price">
                      {cls.includedInSubscription
                        ? s.includedInSubscription
                        : clipBadge
                        ? clipBadge
                        : showKr
                          ? formatMoney(cls.dropInPriceDkk, initData.locale, initData.currency)
                          : priceKind === 'membersOnly' ? s.membershipOnly : s.freeClass}
                    </div>
                    {cta}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}

      {/* Booking runs through the shared modal — handles login inline,
          klip-pris, membership-required + payment-redirect consistently. */}
      {modalKlass && (
        <BookingModal
          klass={modalKlass}
          initData={initData}
          onClose={() => setSelected(null)}
          onBooked={fetchClasses}
          guestsOnly={guestsOnly}
        />
      )}
    </div>
  )
}

const styles = `
  .hsw-sch-user-badge {
    max-width: 1100px;
    margin: 0 auto 1.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #525252);
    text-align: right;
  }
  .hsw-sch-user-badge strong {
    color: var(--yoga-text, #0a0a0a);
    font-weight: 600;
  }

  .hsw-sch-day-group {
    margin-bottom: 2.5rem;
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
  }
  .hsw-sch-day-label {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--yoga-primary, #FF6B35);
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--yoga-primary, #FF6B35);
    margin-bottom: 1rem;
  }

  .hsw-sch-row {
    display: grid;
    grid-template-columns: 130px 1fr auto;
    gap: 1.5rem;
    align-items: center;
    padding: 1.25rem 1.5rem;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    margin-bottom: 0.5rem;
    transition: border-color .3s, box-shadow .3s;
  }
  .hsw-sch-row:hover {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }
  .hsw-sch-row.hsw-sch-row-booked {
    background: rgba(34,197,94,0.04);
    border-color: rgba(34,197,94,0.35);
  }
  .hsw-sch-row.hsw-sch-row-booked:hover {
    border-color: rgba(34,197,94,0.55);
    box-shadow: 0 4px 16px rgba(34,197,94,0.08);
  }
  .hsw-sch-row.hsw-sch-row-full {
    opacity: 0.7;
  }

  .hsw-sch-time {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.95rem;
    letter-spacing: -0.01em;
  }
  .hsw-sch-name {
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    font-size: 1.05rem;
    margin-bottom: 0.25rem;
    letter-spacing: -0.01em;
  }
  .hsw-sch-meta {
    color: var(--yoga-text-muted, #737373);
    font-size: 0.85rem;
  }
  .hsw-sch-spots {
    font-weight: 500;
  }
  .hsw-sch-spots-full {
    color: #dc2626;
    font-weight: 700;
  }
  .hsw-sch-spots-waitlist {
    color: #b45309;
    font-weight: 600;
  }

  .hsw-sch-cta-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
  }
  .hsw-sch-price {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    font-weight: 600;
  }
  .hsw-sch-cta {
    display: inline-block;
    padding: 0.55rem 1.1rem;
    border-radius: var(--yoga-radius, 0);
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.01em;
    border: 1px solid var(--yoga-primary, #FF6B35);
    background: transparent;
    color: var(--yoga-primary, #FF6B35);
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    transition: background .2s, color .2s, border-color .2s;
    font-family: inherit;
  }
  button.hsw-sch-cta { line-height: 1.2; }
  .hsw-sch-cta-book:hover {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
  }
  .hsw-sch-cta-booked {
    border-color: rgba(34,197,94,0.5);
    color: #15803d;
    background: rgba(34,197,94,0.08);
  }
  /* Badgen er nu en knap der aabner modalen (og dermed "Afmeld"), saa den skal
     ogsaa se klikbar ud — den arvede foer cursor: default fra dengang den var
     et <span>. */
  .hsw-sch-cta-booked:hover {
    background: rgba(34,197,94,0.16);
    border-color: rgba(34,197,94,0.8);
  }
  .hsw-sch-cta-waitlist {
    border-color: #d97706;
    color: #b45309;
    background: rgba(245,158,11,0.08);
  }
  .hsw-sch-cta-waitlist:hover {
    background: #d97706;
    color: #fff;
    border-color: #d97706;
  }
  .hsw-sch-cta-full {
    border-color: #d4d4d4;
    color: #737373;
    background: #f5f5f5;
    cursor: not-allowed;
  }

  /* Error */
  .hsw-sch-error {
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 0);
    color: #b91c1c;
    font-size: 0.9rem;
  }
  .hsw-sch-error-icon { font-size: 1.1rem; }

  /* Skeletons */
  .hsw-sch-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-sch-shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
  .hsw-sch-skel-label { height: 14px; width: 30%; margin-bottom: 1rem; }
  .hsw-sch-skel-row { height: 64px; width: 100%; }
  @keyframes hsw-sch-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (max-width: 600px) {
    .hsw-sch-row {
      grid-template-columns: 90px 1fr;
      gap: 0.75rem 1rem;
      padding: 1rem;
    }
    .hsw-sch-cta-wrap {
      grid-column: 1 / -1;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 0.75rem;
      padding-top: 0.5rem;
      border-top: 1px solid #f0f0f0;
    }
    .hsw-sch-price { font-size: 0.95rem; }
  }
`
